import React from 'react';
import ExportSeasonsModal from './ExportSeasonsModal';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Mock Data
const mockDataCompleted = {
  show: true,
  name: 'Winter 2024',
  completed: true,
  failed: false,
};

const mockDataFailed = {
  show: true,
  name: 'Winter 2024',
  completed: true,
  failed: true,
};

const mockDataInProgress = {
  show: true,
  name: 'Winter 2024',
  completed: false,
  failed: false,
};

// Storybook metadata
export default {
  title: 'Components/Seasons/ExportSeasonsModal',
  component: ExportSeasonsModal,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nThe \`ExportSeasonsModal\` component provides a user-friendly interface for exporting seasons.
        \n\n###_**Key Features:**_
        \n- Displays the status of the export process.
        \n- Shows a progress bar while the export is in progress.
        \n- Provides success or failure feedback upon completion.\n\n`,
      // When & how to use it?
      `\n\nUse the \`ExportSeasonsModal\` component when users need to export season data.
        \n\n###_**Key Features:**_
        \n- **Progress Indicator**:
        \n  - Displays an animated progress bar during the export process.
        \n  - Updates the status upon completion or failure.
        \n\n- **Visual Feedback**:
        \n  - Success: Shows a green progress bar and a "Complete!" message.
        \n  - Failure: Displays an error icon and a "Failed" message.
        \n\n- **Close Button**:
        \n  - Allows users to close the modal at any time.\n\n`,
      // Current Usage
      '\nThe `ExportSeasonsModal` component is used on the Seasons page to allow users to export season data. It provides a clear and interactive way to monitor the export status.\n\n',
      // Related Components
      `\n- **[Modal](http://localhost:6006/?path=/docs/components-seasons-modal--docs)**: Base component used for rendering the modal.
      \n- **[Icon](http://localhost:6006/?path=/docs/components-icons-icon--docs)**: Displays relevant icons for export progress and status.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    data: {
      control: { type: 'object' },
      description: 'The data object containing export details (e.g., status, name).',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
    handleClose: {
      control: { type: 'function' },
      description: 'Callback function to close the modal.',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
  },
};

// Template for the stories
const Template = (args) => (
  <div style={{
    height: '50vh', width: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}
  >
    <ExportSeasonsModal {...args} />
  </div>
);

// Story for the completed export
export const ExportCompleted = Template.bind({});
ExportCompleted.args = {
  data: mockDataCompleted,
};
ExportCompleted.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display the modal when the export process has been completed successfully.\n\n',
  // Where is it currently used on the product?
  '\nThe `ExportSeasonsModal` component is used on the Seasons page to show the export status of a season.\n\n',
);

// Story for the failed export
export const ExportFailed = Template.bind({});
ExportFailed.args = {
  data: mockDataFailed,
};
ExportFailed.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display the modal when the export process has failed.\n\n',
  // Where is it currently used on the product?
  '\nThe `ExportSeasonsModal` component is used on the Seasons page to show the export status of a season.\n\n',
);

// Story for the in-progress export
export const ExportInProgress = Template.bind({});
ExportInProgress.args = {
  data: mockDataInProgress,
};
ExportInProgress.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display the modal while the export process is in progress.\n\n',
  // Where is it currently used on the product?
  '\nThe `ExportSeasonsModal` component is used on the Seasons page to show the export status of a season.\n\n',
);
